<template>
  <div class="youtube-videos-container">
    <div class="section-header">
      <h2 class="headline">
        <v-icon left color="primary">mdi-youtube</v-icon>
        Featured YouTube Videos
      </h2>
      <p class="subtitle-1">Explore curated YouTube content for UnStruct.AI</p>
    </div>

    <div v-if="loading" class="loading-container">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <span class="ml-3">Loading videos...</span>
    </div>

    <div v-else-if="youtubeUrls.length === 0" class="no-results">
      <v-icon size="64" color="grey lighten-1">mdi-youtube</v-icon>
      <p class="mt-4 grey--text">No featured videos found yet!</p>
    </div>

    <v-row v-else class="video-grid">
      <v-col
        v-for="(url, index) in youtubeUrls"
        :key="index"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <v-card class="video-card" elevation="2">
          <div class="embed-responsive">
            <iframe
              :src="getEmbedUrl(url)"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              class="embed-responsive-item"
            ></iframe>
            <div class="unstruct-badge">
              <v-icon x-small color="white" class="mr-1">mdi-creation</v-icon>
              <span>Made with UnStruct.AI</span>
            </div>
          </div>
          <v-card-text class="d-flex justify-space-between align-center">
            <v-btn
              small
              text
              color="primary"
              @click="openVideoInNewTab(url)"
            >
              <v-icon left>mdi-open-in-new</v-icon>
              Open in YouTube
            </v-btn>
            <v-btn
              icon
              small
              @click="shareVideo(url)"
            >
              <v-icon>mdi-share-variant</v-icon>
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: 'SimpleYouTubeVideos',

  props: {
    youtubeUrls: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    getEmbedUrl(url) {
      // Extract video ID from various YouTube URL formats
      const videoId = this.extractVideoId(url);
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
      return '';
    },

    extractVideoId(url) {
      // Simple regex to extract video ID from common YouTube URL formats
      const patterns = [
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&\s]+)/,  // Standard watch URL
        /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?\s]+)/,  // Shortened URL
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([^?\s]+)/,  // Shorts URL
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?\s]+)/  // Embedded URL
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          return match[1];
        }
      }

      return null;
    },

    openVideoInNewTab(url) {
      window.open(url, '_blank');
    },

    shareVideo(url) {
      navigator.clipboard.writeText(url);
      this.$store.dispatch('websocket/showTempMessageFn', {
        message: 'Copied video link to clipboard',
        type: 'info',
        timeout: 2000
      });
    }
  }
}
</script>

<style scoped>
.youtube-videos-container {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.headline {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-weight: 500;
}

.subtitle-1 {
  color: rgba(0, 0, 0, 0.6);
}

.theme--dark .subtitle-1 {
  color: rgba(255, 255, 255, 0.7);
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.no-results {
  text-align: center;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.video-card {
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
}

.embed-responsive {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
}

.embed-responsive-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.unstruct-badge {
  position: absolute;
  top: 0px;
  right: 0px;
  background-color: rgba(26, 115, 232, 0.85);
  color: white;
  padding: 2px 8px;
  border-radius: 0 0 0 4px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  z-index: 1;
}

@media (max-width: 768px) {
  .youtube-videos-container {
    padding: 16px;
    padding-bottom: 80px;
  }

  .section-header {
    margin-bottom: 24px;
  }

  .video-grid {
    margin: 0;
  }
}
</style>
